import React from 'react';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';
import AdminHeader from './AdminHeader';
import AdminSidebar from './AdminSidebar';
import { LandingFooter } from '../Landing';

// Fixed 2026-09-02 (real report during prod smoke testing: "the left
// nav-bar is not fixed, it's refreshing for every request"). Before this,
// every admin page (AdminPortal/AdminSettings/AdminStudents/AdminStaff/
// AdminRoles/AdminLexaAnalytics/AdminStudentAvailability) rendered its own
// separate <AdminHeader>/<AdminSidebar>/<LandingFooter> shell, so React
// fully unmounted and remounted all three - including the sidebar - on
// every navigation between admin pages, since react-router treats a
// different `element` per <Route> as a wholly different component tree.
// This layout is now the single shell, held constant across navigation via
// react-router's nested-route <Outlet> - only the routed page's own content
// (the middle `<Outlet />`) unmounts/remounts, sidebar and header stay
// mounted throughout. Each of those 7 pages now returns only its own inner
// content (loading/error/success states), no shell/sidebar of its own.

// AdminSettings.jsx is the one page whose sidebar highlight isn't 1:1 with
// its pathname - it hosts many sections under one route via a ?section=
// query param. Mirrors that file's own VALID_SECTIONS/default-fallback
// logic exactly (kept in sync manually - small, stable list) so the
// highlighted sidebar item matches what AdminSettings itself would have
// passed as activeKey.
const SETTINGS_VALID_SECTIONS = [
  'subjects', 'grades', 'target-exams', 'syllabus', 'concept-hub', 'feature-visibility', 'subscription-plans',
  'system-config', 'question-prompt', 'upload-questions', 'practice-stock', 'exam-stock',
];

const PATH_TO_ACTIVE_KEY = {
  '/admin': 'configure',
  '/admin/students': 'students',
  '/admin/staff': 'staff',
  '/admin/roles': 'roles',
  '/admin/lexa-analytics': 'lexa-analytics',
  '/admin/feedback': 'feedback',
  '/admin/student-availability': 'student-availability',
};

export default function AdminLayout() {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  let activeKey = PATH_TO_ACTIVE_KEY[location.pathname];
  if (location.pathname === '/admin/settings') {
    const section = searchParams.get('section');
    activeKey = SETTINGS_VALID_SECTIONS.includes(section) ? section : 'subjects';
  }

  return (
    <div className="min-h-screen bg-white text-navy flex flex-col">
      <AdminHeader />
      <div className="flex-1">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 flex flex-col md:flex-row gap-6">
          <AdminSidebar activeKey={activeKey} />
          <div className="flex-1 min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
